import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { z } from 'zod';
import { Operation, useLoader } from '../../../../../redux';
import { showDangerToast, showSuccessToast } from '../../../../../utils';
import { SimpleButton } from '../../../../common/Buttons';
import {
  localizedStringOptional,
  requiredString,
  submitFormByRef,
} from '../../../../common/Forms';
import { useDirtyFormBlockNavigation } from '../../../../common/Forms/NavigationBlocker';
import { InputField } from '../../../../common/Inputs';
import { FormRow, Row } from '../../../../common/LayoutComponents';
import { useCreateTimingPlace } from './useCreateTimingPlace';

const testIds = {
  label: 'CreateTimingPlaceForm::label',
  finnishDescription: 'CreateTimingPlaceForm::finnishDescription',
  submitButton: 'CreateTimingPlaceForm::submitButton',
  cancelButton: 'CreateTimingPlaceForm::cancelButton',
};

const schema = z.object({
  label: requiredString,
  description: localizedStringOptional,
});

type FormState = z.infer<typeof schema>;

type CreateTimingPlaceFormProps = {
  readonly className?: string;
  readonly onCancel: () => void;
  readonly onTimingPlaceCreated: (timingPlaceId: UUID) => void;
};

export const CreateTimingPlaceForm: FC<CreateTimingPlaceFormProps> = ({
  className,
  onCancel,
  onTimingPlaceCreated,
}) => {
  const { t } = useTranslation();
  const formRef = useRef<ExplicitAny>(null);
  const { setIsLoading } = useLoader(Operation.SaveTimingPlace);

  const { prepareCreate, insertTimingPlaceMutation, defaultErrorHandler } =
    useCreateTimingPlace();

  const defaultValues = { label: '', description: { fi_FI: '', sv_FI: '' } };

  const methods = useForm<FormState>({
    defaultValues,
    resolver: zodResolver(schema),
  });
  useDirtyFormBlockNavigation(methods.formState, 'CreateTimingPlaceForm', {
    allowSearchChange: true, // Allow search change so that moving the map does not show the navigation blocked dialog
    allowStateChange: true, // Allow state change so that location state updates do not show the navigation blocked dialog
  });
  const { handleSubmit } = methods;

  const onCreate = async (state: FormState) =>
    prepareCreate({
      input: {
        ...state,
      },
    });

  const onFormSubmit = async (state: FormState) => {
    setIsLoading(true);

    try {
      const changes = await onCreate(state);

      if (changes.conflicts?.length) {
        showDangerToast(t(($) => $.timingPlaces.labelReserved));
        return;
      }

      const createResponse = await insertTimingPlaceMutation(changes);

      showSuccessToast(t(($) => $.timingPlaces.saveSuccess));

      const createdTimingPlaceId =
        // If creation is successful, response contains created timing place.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        createResponse.data!.insert_timing_pattern_timing_place_one!
          .timing_place_id;

      onTimingPlaceCreated(createdTimingPlaceId);
    } catch (err) {
      defaultErrorHandler(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSave = () => {
    submitFormByRef(formRef);
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onFormSubmit)}
        ref={formRef}
        className={twMerge('space-y-4', className)}
      >
        <FormRow>
          <InputField<FormState>
            type="text"
            translationPrefix="timingPlaces"
            fieldPath="label"
            testId={testIds.label}
            required
          />
        </FormRow>
        <FormRow>
          <InputField<FormState>
            type="text"
            translationPrefix="timingPlaces"
            fieldPath="description.fi_FI"
            testId={testIds.finnishDescription}
          />
        </FormRow>
        <Row className="justify-end gap-4">
          <SimpleButton
            inverted
            onClick={onCancel}
            testId={testIds.cancelButton}
          >
            {t(($) => $.cancel)}
          </SimpleButton>
          <SimpleButton onClick={onSave} testId={testIds.submitButton}>
            {t(($) => $.timingPlaces.create)}
          </SimpleButton>
        </Row>
      </form>
    </FormProvider>
  );
};
